// Вертикальный bar-chart для временных рядов (Статистика — docs/PLAN.md, запрос пользователя
// 2026-08-21) — один хаб (сравнение величины во времени), один цвет (sequential, см. dataviz
// skill: "compare magnitude" → bar, "sequential (one hue)"), без легенды (одна серия — заголовок
// карточки уже называет, что на графике). Спеки марок — оттуда же: столбик ≤24px, 4px скругление
// сверху, квадратный низ (растёт от базовой линии), hairline-сетка, per-bar hover tooltip вместо
// crosshair (у бара нет "оси X, между точками которой целится курсор" — у линии есть, у столбцов
// сам столбик — цель наведения).
import { useState } from "react";

const HEIGHT = 180;
const BAR_MAX_WIDTH = 24;
const GAP = 4; // surface gap между соседними барами

function niceMax(value: number): number {
  if (value <= 0) {
    return 1;
  }
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return step * magnitude;
}

export function BarChart({
  data,
  color = "var(--color-primary)",
}: {
  data: { label: string; value: number }[];
  color?: string;
}) {
  const [hovered, setHovered] = useState<number | null>(null);

  if (data.length === 0) {
    return <p style={{ color: "var(--text-muted)" }}>Нет данных за выбранный период.</p>;
  }

  const max = niceMax(Math.max(...data.map((d) => d.value), 1));
  const gridLines = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(max * f));

  const width = Math.max(data.length * (BAR_MAX_WIDTH + GAP), 320);
  const barWidth = Math.min(BAR_MAX_WIDTH, width / data.length - GAP);
  // Не все подписи оси X — иначе на 30+ днях они налезают друг на друга (dataviz skill:
  // "label selectively"). Показываем примерно 6-8 меток, остальное несёт tooltip по наведению.
  const labelStride = Math.max(1, Math.ceil(data.length / 8));

  return (
    <div style={{ position: "relative" }}>
      <svg
        viewBox={`0 0 ${width} ${HEIGHT + 24}`}
        width="100%"
        height={HEIGHT + 24}
        role="img"
        aria-label="Столбчатая диаграмма"
      >
        {gridLines.map((g) => {
          const y = HEIGHT - (g / max) * HEIGHT;
          return (
            <g key={g}>
              <line x1={0} x2={width} y1={y} y2={y} stroke="var(--color-border)" strokeWidth={1} />
              <text x={0} y={y - 4} fontSize={10} fill="var(--color-text-secondary)">
                {g}
              </text>
            </g>
          );
        })}

        {data.map((point, index) => {
          const barHeight = max === 0 ? 0 : (point.value / max) * HEIGHT;
          const x = index * (width / data.length) + (width / data.length - barWidth) / 2;
          const y = HEIGHT - barHeight;
          const isHovered = hovered === index;
          return (
            <g key={point.label}>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={Math.max(barHeight, 1)}
                rx={4}
                ry={4}
                fill={color}
                opacity={isHovered ? 1 : 0.85}
                onPointerEnter={() => setHovered(index)}
                onPointerLeave={() => setHovered(null)}
                style={{ cursor: "pointer" }}
              />
              {index % labelStride === 0 && (
                <text
                  x={x + barWidth / 2}
                  y={HEIGHT + 16}
                  fontSize={10}
                  textAnchor="middle"
                  fill="var(--color-text-secondary)"
                >
                  {point.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {hovered !== null && (
        <div
          style={{
            position: "absolute",
            left: `${((hovered + 0.5) / data.length) * 100}%`,
            top: 0,
            transform: "translate(-50%, -100%)",
            background: "var(--color-text-primary)",
            color: "var(--color-bg)",
            padding: "4px 8px",
            borderRadius: "var(--radius-sm)",
            fontSize: 12,
            whiteSpace: "nowrap",
            pointerEvents: "none",
          }}
        >
          <strong>{data[hovered].value}</strong> · {data[hovered].label}
        </div>
      )}
    </div>
  );
}
